<template>
  <div class="device-frame-page">
    <div class="device-frame-stage" :style="stageStyle">
      <p class="device-frame-tagline">
        Scrawl is built for your phone - here's a peek.
      </p>
      <div class="device-frame">
        <div class="device-frame-bezel">
          <span class="device-btn device-btn-volume-up" aria-hidden="true"></span>
          <span class="device-btn device-btn-volume-down" aria-hidden="true"></span>
          <span class="device-btn device-btn-power" aria-hidden="true"></span>
          <iframe
            :src="iframeSrc"
            class="device-frame-screen"
            title="Scrawl"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Reference phone dimensions (iPhone 14 logical resolution)
const PHONE_W = 390;
const PHONE_H = 844;
const BEZEL_PAD = 14;
// Approximate stage height: tagline (~22px) + gap (18px) + bezel
const STAGE_H = 22 + 18 + PHONE_H + BEZEL_PAD * 2;
const BEZEL_W = PHONE_W + BEZEL_PAD * 2;

export default {
  name: "DesktopFrame",
  data() {
    return { scale: 1 };
  },
  computed: {
    iframeSrc() {
      const url = new URL(window.location.href);
      url.searchParams.set("_phoneview", "1");
      return url.toString();
    },
    stageStyle() {
      return { zoom: this.scale };
    },
  },
  mounted() {
    this.updateScale();
    window.addEventListener("resize", this.updateScale);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateScale);
  },
  methods: {
    updateScale() {
      const scaleH = (window.innerHeight - 40) / STAGE_H;
      const scaleW = (window.innerWidth - 40) / BEZEL_W;
      this.scale = Math.min(1, scaleH, scaleW);
    },
  },
};
</script>

<style lang="scss" scoped>
// Soft Y2K Bubble — pixel-matched to mock screen 26 (Desktop Frame) at
// /mocks/redesign-4-soft-y2k-bubble.html lines 3508+ and the mock's
// `.phone` / `.phone::before` iridescent-bezel pattern (lines 223–251).
// The body's animated V2 sky lives in main.scss — we keep the page
// transparent so it bleeds through, layering decorative blurred blobs
// on top for scene depth.

.device-frame-page {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  padding: 20px;
  background: transparent;

  // Decorative blurred-blob scene — sits behind the bezel, on top of the
  // body sky. Mirrors the page-blob treatment in DesktopSplash.vue.
  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    filter: blur(120px);
    opacity: 0.5;
    pointer-events: none;
    z-index: 0;
  }

  &::before {
    width: 480px;
    height: 480px;
    background: $bubblegum-soft;
    top: -120px;
    left: -120px;
  }

  &::after {
    width: 420px;
    height: 420px;
    background: $mint;
    bottom: -100px;
    right: -100px;
  }
}

.device-frame-stage {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}

.device-frame-tagline {
  margin: 0;
  font-family: $font-body;
  font-size: 13px;
  font-weight: 600;
  color: $ink-soft;
  letter-spacing: 0.01em;
  background: rgba(255, 255, 255, 0.7);
  padding: 6px 14px;
  border-radius: $radius-pill;
  border: 1px solid rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

// Fixed at the reference phone size — zoom on the stage handles all scaling.
.device-frame {
  width: 440px; // 412 + 2×14 bezel padding
  height: 872px; // 844 + 2×14 bezel padding
}

// Black device bezel — matches the original treatment (the user prefers
// the solid dark device chrome over the V2 white-glass variant). The
// near-black `#1a1330` is the same hue as the mock's `.notch` glyph,
// keeping the device chrome cohesive against the V2 pastel sky.
.device-frame-bezel {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 14px;
  border-radius: 56px;
  background: #1a1330;
  box-shadow:
    0 30px 60px -25px rgba($shadow-purple, 0.4),
    0 8px 20px -10px rgba(0, 0, 0, 0.25),
    inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  z-index: 1;
}

// Inner screen — radius matches mock `.phone .screen` (46px), which sits
// inside a 56px outer bezel with 14px padding. The pastel base shows
// briefly while the iframe loads, then the SPA paints over it.
.device-frame-screen {
  width: 412px;
  height: 844px;
  border: none;
  border-radius: 46px;
  display: block;
  background:
    radial-gradient(60% 40% at 20% 10%, $sky-2 0%, transparent 60%),
    radial-gradient(70% 50% at 90% 30%, $sky-1 0%, transparent 60%),
    radial-gradient(60% 60% at 30% 90%, $sky-3 0%, transparent 60%),
    linear-gradient(180deg, $surface-1 0%, $surface-2 50%, $surface-3 100%);
  isolation: isolate;
}

// Mock 26 has no side buttons — the Y2K bezel reads as a glassy slab, not a
// physical phone. The decorative span markup is kept so JS / template stay
// untouched, but visually suppressed.
.device-btn {
  display: none;
}
</style>
